import { useState, useEffect, useCallback } from 'react';
import API from '../api/axios';
import toast from 'react-hot-toast';
import ArchiveManager from '../components/ArchiveManager';
import BannerManager from '../components/BannerManager';
import CategoryManager from '../components/CategoryManager';
import {
  Package, Users, ShoppingBag, TrendingUp,
  Plus, Pencil, Trash2, X, Check, ChevronDown, RefreshCw, Upload
} from 'lucide-react';

const STATUS_OPTIONS = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];
const STATUS_COLORS = {
  pending:   { color: '#F39C12', bg: '#FEF9E7' },
  confirmed: { color: '#2980B9', bg: '#EBF5FB' },
  shipped:   { color: '#8E44AD', bg: '#F5EEF8' },
  delivered: { color: '#27AE60', bg: '#EAFAF1' },
  cancelled: { color: '#C0392B', bg: '#FDEDEC' },
};

const EMPTY_PRODUCT = { name: '', description: '', price: '', stock: '', category_id: '', image_url: '', image_urls: [], has_sizes: false, size_options: '', custom_fields: {}, low_stock_threshold: 5, is_visible: true };

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [dataError, setDataError] = useState('');
  const [loadingData, setLoadingData] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [savingProduct, setSavingProduct] = useState(false);
  const [uploadingProductImages, setUploadingProductImages] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoadingData(true);
    const results = await Promise.allSettled([
      API.get('/products/admin/all'),
      API.get('/products/categories'),
      API.get('/orders/all'),
      API.get('/products/admin/low-stock'),
    ]);
    const [productsResult, categoriesResult, ordersResult, lowStockResult] = results;
    if (productsResult.status === 'fulfilled') setProducts(productsResult.value.data);
    if (categoriesResult.status === 'fulfilled') setCategories(categoriesResult.value.data);
    if (ordersResult.status === 'fulfilled') setOrders(ordersResult.value.data);
    if (lowStockResult.status === 'fulfilled') setLowStockProducts(lowStockResult.value.data);

    const failedSections = [
      ['products', productsResult],
      ['categories', categoriesResult],
      ['orders', ordersResult],
      ['stock alerts', lowStockResult],
    ].filter(([, result]) => result.status === 'rejected').map(([section]) => section);

    setDataError(failedSections.join(', '));
    if (failedSections.length) toast.error(`Could not load ${failedSections.join(', ')}. Retry below.`);
    setLoadingData(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Products ──────────────────────────────────────────
  const openAddProduct = () => {
    setEditingProduct(null);
    setProductForm(EMPTY_PRODUCT);
    setShowProductModal(true);
  };

  const openEditProduct = (p) => {
    setEditingProduct(p);
    setProductForm({
      name: p.name, description: p.description || '',
      price: p.price, stock: p.stock,
      category_id: p.category_id || '',
      image_url: p.image_url || '',
      image_urls: p.image_urls?.length ? p.image_urls : (p.image_url ? [p.image_url] : []),
      has_sizes: p.has_sizes || false,
      size_options: p.size_options || '',
      custom_fields: p.custom_fields || {},
      low_stock_threshold: p.low_stock_threshold,
      is_visible: p.is_visible,
    });
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) {
      toast.error('Name and price are required'); return;
    }
    setSavingProduct(true);
    try {
      const customFields = Object.fromEntries(Object.entries(productForm.custom_fields || {}).filter(([key, value]) => key.trim() && String(value).trim()));
      const imageUrls = (productForm.image_urls || []).slice(0, 6);
      const payload = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock, 10) || 0,
        low_stock_threshold: parseInt(productForm.low_stock_threshold, 10) || 0,
        category_id: productForm.category_id || null,
        image_urls: imageUrls,
        image_url: imageUrls[0] || '',
        size_options: productForm.has_sizes ? productForm.size_options : null,
        custom_fields: customFields,
      };
      if (editingProduct) {
        await API.put(`/products/${editingProduct.id}`, payload);
        toast.success('Product updated!');
      } else {
        await API.post('/products/', payload);
        toast.success('Product created!');
      }
      setShowProductModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    } finally { setSavingProduct(false); }
  };

  const handleProductPhotoUpload = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    const currentPhotos = productForm.image_urls || [];
    if (currentPhotos.length + files.length > 6) {
      toast.error(`You can add ${6 - currentPhotos.length} more photo${6 - currentPhotos.length === 1 ? '' : 's'}`);
      return;
    }
    const invalidFile = files.find(file => !file.type.startsWith('image/') || file.size > 10 * 1024 * 1024);
    if (invalidFile) {
      toast.error('Choose image files no larger than 10 MB each');
      return;
    }

    setUploadingProductImages(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const { data: signature } = await API.post('/products/admin/upload-signature');
        const payload = new FormData();
        payload.append('file', file);
        payload.append('api_key', signature.api_key);
        payload.append('timestamp', signature.timestamp);
        payload.append('folder', signature.folder);
        payload.append('upload_preset', signature.upload_preset);
        payload.append('signature', signature.signature);
        const response = await fetch(`https://api.cloudinary.com/v1_1/${signature.cloud_name}/image/upload`, { method: 'POST', body: payload });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error?.message || 'Photo upload failed');
        uploadedUrls.push(result.secure_url);
      }
      const nextPhotos = [...currentPhotos, ...uploadedUrls].slice(0, 6);
      setProductForm(current => ({ ...current, image_urls: nextPhotos, image_url: nextPhotos[0] || '' }));
      toast.success(`${uploadedUrls.length} product photo${uploadedUrls.length === 1 ? '' : 's'} uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Product photo upload failed');
    } finally {
      setUploadingProductImages(false);
    }
  };

  const removeProductPhoto = index => {
    const image_urls = productForm.image_urls.filter((_, photoIndex) => photoIndex !== index);
    setProductForm(current => ({ ...current, image_urls, image_url: image_urls[0] || '' }));
  };

  const updateCustomField = (index, field, value) => {
    const fields = Object.entries(productForm.custom_fields || {});
    const current = fields[index] || ['', ''];
    fields[index] = field === 'name' ? [value, current[1]] : [current[0], value];
    setProductForm(currentForm => ({
      ...currentForm,
      custom_fields: Object.fromEntries(fields.filter(([key]) => key.trim())),
    }));
  };

  const addCustomField = () => {
    setProductForm(current => ({
      ...current,
      custom_fields: { ...current.custom_fields, [`Field ${Object.keys(current.custom_fields || {}).length + 1}`]: '' },
    }));
  };

  const handleDeleteProduct = async (id) => {
    const product = products.find(item => item.id === id);
    if (!product || !window.confirm(`Move “${product.name}” to the archive? It can be restored later.`)) return;
    if (window.prompt(`Type the product name to confirm: ${product.name}`) !== product.name) return;
    try {
      await API.post(`/products/${id}/archive`);
      toast.success('Product archived');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete product');
    }
  };

  const handleVisibilityChange = async product => {
    try {
      await API.patch(`/products/${product.id}/visibility`, { is_visible: !product.is_visible });
      toast.success(product.is_visible ? 'Product hidden from customers' : 'Product visible to customers');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update product visibility');
    }
  };

  const handleArchiveOrder = async order => {
    if (!['delivered', 'cancelled'].includes(order.status)) {
      toast.error('Only delivered or cancelled orders can be archived');
      return;
    }
    if (!window.confirm(`Archive order #${order.id}? It will remain in the archive and can be restored.`)) return;
    if (window.prompt(`Type ${order.status} to confirm archiving order #${order.id}:`) !== order.status) return;
    try {
      await API.post(`/orders/${order.id}/archive`, { confirm_status: order.status });
      toast.success(`Order #${order.id} archived`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not archive order');
    }
  };

  // ── Orders ────────────────────────────────────────────
  const handleStatusChange = async (orderId, status) => {
    try {
      await API.put(`/orders/${orderId}/status?status=${status}`);
      toast.success(`Order #${orderId} → ${status}`);
      fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  // ── Stats ─────────────────────────────────────────────
  const totalRevenue = orders
    .filter(o => o.status !== 'cancelled')
    .reduce((sum, o) => sum + parseFloat(o.total_amount), 0);

  const tabStyle = (tab) => ({
    padding: '0.6rem 1.4rem', border: 'none', cursor: 'pointer',
    borderBottom: activeTab === tab ? '3px solid var(--soft-gold)' : '3px solid transparent',
    background: 'transparent',
    fontWeight: activeTab === tab ? '700' : '500',
    fontSize: '0.9rem',
    color: activeTab === tab ? 'var(--charcoal)' : 'var(--mid-gray)',
    transition: 'var(--transition)'
  });

  const inputStyle = {
    width: '100%', padding: '0.65rem 0.9rem',
    border: '1.5px solid var(--light-gray)',
    borderRadius: '8px', fontSize: '0.9rem',
    outline: 'none', background: 'var(--cream)',
    fontFamily: 'var(--font-body)'
  };

  return (
    <div style={{ background: 'var(--cream)', minHeight: 'calc(100vh - 70px)', padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{
          background: 'var(--charcoal)', borderRadius: 'var(--radius)',
          padding: '1.8rem 2rem', marginBottom: '2rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--white)', marginBottom: '0.2rem' }}>
              Admin Dashboard
            </h1>
            <p style={{ color: 'var(--mid-gray)', fontSize: '0.9rem' }}>Manage your Kraftova store</p>
          </div>
          <span style={{
            background: 'var(--soft-gold)', color: 'var(--charcoal)',
            padding: '0.3rem 1rem', borderRadius: '20px',
            fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase'
          }}>Admin</span>
        </div>

        {dataError && (
          <div role="alert" style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: '1rem', padding: '0.9rem 1rem', marginBottom: '1.25rem',
            background: '#FEF3E7', border: '1px solid #F2C078', borderRadius: '8px'
          }}>
            <span>Could not load {dataError}. Your other admin data is still available.</span>
            <button type="button" onClick={fetchAll} disabled={loadingData} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.5rem 0.75rem', border: '1px solid var(--light-gray)',
              borderRadius: '6px', background: 'var(--white)', cursor: loadingData ? 'wait' : 'pointer'
            }}>
              <RefreshCw size={15} /> {loadingData ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        )}

        {lowStockProducts.length > 0 && (
          <div role="status" style={{
            padding: '0.9rem 1rem', marginBottom: '1.25rem',
            background: '#FFF6E5', border: '1px solid #E8C36A', borderRadius: '8px'
          }}>
            <strong>{lowStockProducts.length} low-stock alert{lowStockProducts.length === 1 ? '' : 's'}:</strong>{' '}
            {lowStockProducts.map(product => `${product.name} (${product.stock}/${product.low_stock_threshold})`).join(' · ')}
          </div>
        )}

        {/* Stats Cards */}
        {[
          { label: 'Total Products', value: products.length, icon: ShoppingBag, color: '#2980B9' },
          { label: 'Total Orders', value: orders.length, icon: Package, color: '#8E44AD' },
          { label: 'Revenue', value: `₹${totalRevenue.toFixed(2)}`, icon: TrendingUp, color: '#27AE60' },
          { label: 'Categories', value: categories.length, icon: Users, color: '#F39C12' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} style={{
            display: 'inline-flex', flexDirection: 'column',
            background: 'var(--white)', borderRadius: 'var(--radius)',
            padding: '1.4rem 1.8rem', marginRight: '1rem', marginBottom: '1.5rem',
            boxShadow: 'var(--shadow-sm)', minWidth: '180px',
            borderTop: `4px solid ${color}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
              <Icon size={18} style={{ color }} />
              <span style={{ fontSize: '0.82rem', color: 'var(--mid-gray)', fontWeight: '500' }}>{label}</span>
            </div>
            <span style={{ fontSize: '1.8rem', fontWeight: '700', fontFamily: 'var(--font-display)', color: 'var(--charcoal)' }}>
              {value}
            </span>
          </div>
        ))}

        {/* Tabs */}
        <div style={{
          background: 'var(--white)', borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-sm)', overflow: 'hidden'
        }}>
          <div style={{ display: 'flex', borderBottom: '1px solid var(--light-gray)', padding: '0 1rem' }}>
            {['overview', 'products', 'categories', 'orders', 'banners', 'archive'].map(tab => (
              <button key={tab} style={tabStyle(tab)} onClick={() => setActiveTab(tab)}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div style={{ padding: '2rem' }}>

            {/* ── OVERVIEW TAB ── */}
            {activeTab === 'overview' && (
              <div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', marginBottom: '1rem' }}>
                  Recent Orders
                </h3>
                {orders.slice(0, 5).map(order => {
                  const cfg = STATUS_COLORS[order.status] || STATUS_COLORS.pending;
                  return (
                    <div key={order.id} style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', padding: '0.9rem 0',
                      borderBottom: '1px solid var(--light-gray)'
                    }}>
                      <div>
                        <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>Order #{order.id}</span>
                        <span style={{ color: 'var(--mid-gray)', fontSize: '0.82rem', marginLeft: '0.8rem' }}>
                          {order.items?.length} items
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{
                          background: cfg.bg, color: cfg.color,
                          padding: '0.2rem 0.7rem', borderRadius: '20px',
                          fontSize: '0.75rem', fontWeight: '600', textTransform: 'capitalize'
                        }}>{order.status}</span>
                        <span style={{ fontWeight: '700', color: 'var(--warm-brown)' }}>
                          ₹{parseFloat(order.total_amount).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── PRODUCTS TAB ── */}
            {activeTab === 'products' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem' }}>
                    Products ({products.length})
                  </h3>
                  <button onClick={openAddProduct} style={{
                    background: 'var(--charcoal)', color: 'var(--white)',
                    border: 'none', padding: '0.6rem 1.2rem',
                    borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    <Plus size={16} /> Add Product
                  </button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--light-gray)', textAlign: 'left' }}>
                        {['Image', 'Name', 'Category', 'Price', 'Stock / Alert', 'Visibility', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '0.7rem 0.8rem', color: 'var(--mid-gray)', fontWeight: '600', fontSize: '0.82rem', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid var(--light-gray)' }}
                          onMouseOver={e => e.currentTarget.style.background = 'var(--cream)'}
                          onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '0.7rem 0.8rem' }}>
                            <img src={p.image_url || 'https://via.placeholder.com/50'} alt={p.name}
                              style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' }} />
                          </td>
                          <td style={{ padding: '0.7rem 0.8rem', fontWeight: '500' }}>{p.name}</td>
                          <td style={{ padding: '0.7rem 0.8rem', color: 'var(--mid-gray)' }}>{p.category?.name || '—'}</td>
                          <td style={{ padding: '0.7rem 0.8rem', fontWeight: '600', color: 'var(--warm-brown)' }}>
                            ₹{parseFloat(p.price).toFixed(2)}
                          </td>
                          <td style={{ padding: '0.7rem 0.8rem' }}>
                            <span style={{ color: p.stock < 10 ? 'var(--danger)' : 'var(--success)', fontWeight: '500' }}>
                              {p.stock} / {p.low_stock_threshold}
                            </span>
                          </td>
                          <td style={{ padding: '0.7rem 0.8rem' }}>
                            <button type="button" onClick={() => handleVisibilityChange(p)} style={{
                              padding: '0.35rem 0.6rem', border: '1px solid var(--light-gray)',
                              borderRadius: '6px', background: p.is_visible ? '#EAF7EF' : '#F2F2F2', cursor: 'pointer'
                            }}>{p.is_visible ? 'Visible' : 'Hidden'}</button>
                          </td>
                          <td style={{ padding: '0.7rem 0.8rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button onClick={() => openEditProduct(p)} style={{
                                background: 'var(--light-gray)', border: 'none',
                                padding: '0.35rem 0.7rem', borderRadius: '6px',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                                fontSize: '0.82rem', fontWeight: '500'
                              }}>
                                <Pencil size={13} /> Edit
                              </button>
                              <button onClick={() => handleDeleteProduct(p.id)} style={{
                                background: '#FDEDEC', color: 'var(--danger)', border: 'none',
                                padding: '0.35rem 0.7rem', borderRadius: '6px',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                                fontSize: '0.82rem', fontWeight: '500'
                              }}>
                                <Trash2 size={13} /> Archive
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── ORDERS TAB ── */}
            {activeTab === 'orders' && (
              <div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', marginBottom: '1.5rem' }}>
                  All Orders ({orders.length})
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  {orders.map(order => {
                    const cfg = STATUS_COLORS[order.status] || STATUS_COLORS.pending;
                    const isExpanded = expandedOrder === order.id;
                    return (
                      <div key={order.id} style={{
                        border: '1.5px solid var(--light-gray)',
                        borderRadius: 'var(--radius)', overflow: 'hidden'
                      }}>
                        <div style={{
                          padding: '1rem 1.2rem', display: 'flex',
                          alignItems: 'center', gap: '1rem',
                          background: isExpanded ? 'var(--light-gray)' : 'var(--white)',
                          cursor: 'pointer'
                        }} onClick={() => setExpandedOrder(isExpanded ? null : order.id)}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.2rem' }}>
                              <span style={{ fontWeight: '700' }}>Order #{order.id}</span>
                              <span style={{
                                background: cfg.bg, color: cfg.color,
                                padding: '0.2rem 0.7rem', borderRadius: '20px',
                                fontSize: '0.75rem', fontWeight: '600', textTransform: 'capitalize'
                              }}>{order.status}</span>
                            </div>
                            <p style={{ fontSize: '0.82rem', color: 'var(--mid-gray)' }}>
                              {new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                              {' · '}{order.items?.length} items
                            </p>
                          </div>

                          {/* Status Dropdown */}
                          <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                            <select
                              value={order.status}
                              onChange={e => handleStatusChange(order.id, e.target.value)}
                              style={{
                                padding: '0.4rem 2rem 0.4rem 0.8rem',
                                border: '1.5px solid var(--light-gray)',
                                borderRadius: '8px', fontSize: '0.85rem',
                                background: 'var(--white)', cursor: 'pointer',
                                outline: 'none', fontFamily: 'var(--font-body)',
                                appearance: 'none', fontWeight: '500'
                              }}>
                              {STATUS_OPTIONS.map(s => (
                                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                              ))}
                            </select>
                            <ChevronDown size={14} style={{
                              position: 'absolute', right: '8px', top: '50%',
                              transform: 'translateY(-50%)', pointerEvents: 'none',
                              color: 'var(--mid-gray)'
                            }} />
                          </div>

                          {['delivered', 'cancelled'].includes(order.status) && (
                            <button type="button" onClick={event => { event.stopPropagation(); handleArchiveOrder(order); }} style={{
                              ...inputStyle, width: 'auto', padding: '0.4rem 0.65rem', color: 'var(--danger)', cursor: 'pointer'
                            }}>Archive</button>
                          )}
                          <span style={{ fontWeight: '700', color: 'var(--warm-brown)', minWidth: '80px', textAlign: 'right' }}>
                            ₹{parseFloat(order.total_amount).toFixed(2)}
                          </span>
                        </div>

                        {isExpanded && (
                          <div style={{ padding: '1rem 1.2rem', borderTop: '1px solid var(--light-gray)', background: '#FDFCFA' }}>
                            {order.shipping_address && (
                              <p style={{ fontSize: '0.85rem', color: 'var(--mid-gray)', marginBottom: '0.8rem' }}>
                                📍 {order.shipping_address}
                              </p>
                            )}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {order.items?.map(item => (
                                <div key={item.id} style={{
                                  display: 'flex', alignItems: 'center', gap: '0.8rem',
                                  padding: '0.5rem', background: 'var(--white)',
                                  borderRadius: '8px', border: '1px solid var(--light-gray)'
                                }}>
                                  <img src={item.product?.image_url || 'https://via.placeholder.com/48'}
                                    alt={item.product?.name}
                                    style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '6px' }} />
                                  <div style={{ flex: 1 }}>
                                    <p style={{ fontWeight: '600', fontSize: '0.88rem' }}>{item.product?.name}</p>
                                    <p style={{ fontSize: '0.78rem', color: 'var(--mid-gray)' }}>
                                      Qty: {item.quantity}{item.size && ` · Size: ${item.size}`}
                                    </p>
                                  </div>
                                  <span style={{ fontWeight: '600', fontSize: '0.88rem', color: 'var(--warm-brown)' }}>
                                    ₹{(parseFloat(item.unit_price) * item.quantity).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'banners' && <BannerManager />}
            {activeTab === 'categories' && <CategoryManager categories={categories} onChanged={fetchAll} />}
            {activeTab === 'archive' && <ArchiveManager />}
          </div>
        </div>
      </div>

      {/* ── PRODUCT MODAL ── */}
      {showProductModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '1rem'
        }}>
          <div style={{
            background: 'var(--white)', borderRadius: '16px',
            padding: '2rem', width: '100%', maxWidth: '760px',
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: 'var(--shadow-lg)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem' }}>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={() => setShowProductModal(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mid-gray)'
              }}>
                <X size={22} />
              </button>
            </div>

            {[
              { label: 'Product Name *', key: 'name', type: 'text', placeholder: 'e.g. Classic White Shirt' },
              { label: 'Price (₹) *', key: 'price', type: 'number', placeholder: '2999' },
              { label: 'Stock Quantity', key: 'stock', type: 'number', placeholder: '50' },
              { label: 'Low Stock Alert At', key: 'low_stock_threshold', type: 'number', placeholder: '5' },
            ].map(({ label, key, type, placeholder }) => (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: '500', fontSize: '0.85rem', marginBottom: '0.35rem' }}>{label}</label>
                <input type={type} placeholder={placeholder} value={productForm[key]}
                  onChange={e => setProductForm({ ...productForm, [key]: e.target.value })}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--warm-brown)'}
                  onBlur={e => e.target.style.borderColor = 'var(--light-gray)'}
                />
              </div>
            ))}

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <input type="checkbox" checked={productForm.is_visible}
                onChange={event => setProductForm({ ...productForm, is_visible: event.target.checked })} />
              Visible to customers
            </label>

            <section style={{ margin: '1rem 0', padding: '1rem', background: 'var(--cream)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '0.8rem' }}>
                <div><strong>Product photos</strong><div style={{ color: 'var(--mid-gray)', fontSize: '0.82rem' }}>{(productForm.image_urls || []).length} of 6 · first photo is the cover</div></div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 0.75rem', borderRadius: '6px', background: 'var(--charcoal)', color: 'var(--white)', cursor: uploadingProductImages || (productForm.image_urls || []).length >= 6 ? 'not-allowed' : 'pointer', opacity: uploadingProductImages || (productForm.image_urls || []).length >= 6 ? 0.6 : 1 }}>
                  <Upload size={15} /> {uploadingProductImages ? 'Uploading...' : 'Upload photos'}
                  <input type="file" accept="image/*" multiple disabled={uploadingProductImages || (productForm.image_urls || []).length >= 6} onChange={handleProductPhotoUpload} style={{ display: 'none' }} />
                </label>
              </div>
              {!!(productForm.image_urls || []).length && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.6rem' }}>
                {productForm.image_urls.map((url, index) => <div key={`${url}-${index}`} style={{ position: 'relative' }}>
                  <img src={url} alt={`Product preview ${index + 1}`} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '6px', border: index === 0 ? '2px solid var(--soft-gold)' : '1px solid var(--light-gray)' }} />
                  <button type="button" onClick={() => removeProductPhoto(index)} aria-label={`Remove photo ${index + 1}`} style={{ position: 'absolute', top: '4px', right: '4px', width: '26px', height: '26px', border: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', color: 'white', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><X size={15} /></button>
                  {index === 0 && <small style={{ position: 'absolute', left: '4px', bottom: '4px', background: 'rgba(0,0,0,0.7)', color: 'white', padding: '2px 5px', borderRadius: '3px' }}>Cover</small>}
                </div>)}
              </div>}
            </section>

            <section style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid var(--light-gray)', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                <input type="checkbox" checked={productForm.has_sizes} onChange={event => setProductForm(current => ({ ...current, has_sizes: event.target.checked, size_options: event.target.checked ? (current.size_options || '') : '' }))} />
                This product has selectable sizes or variants
              </label>
              {productForm.has_sizes && <label style={{ display: 'block', marginTop: '0.7rem' }}>Options, separated by commas
                <input type="text" required placeholder="e.g. Small, Medium, Large" value={productForm.size_options} onChange={event => setProductForm({ ...productForm, size_options: event.target.value })} style={inputStyle} />
              </label>}
            </section>

            <section style={{ marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div><strong>Extra product details</strong><div style={{ color: 'var(--mid-gray)', fontSize: '0.82rem' }}>Add attributes such as material, craft, occasion, dimensions, or origin.</div></div>
                <button type="button" onClick={addCustomField} style={{ border: '1px solid var(--light-gray)', borderRadius: '6px', padding: '0.45rem 0.65rem', background: 'var(--white)', cursor: 'pointer' }}><Plus size={14} /> Add field</button>
              </div>
              {Object.entries(productForm.custom_fields || {}).map(([fieldName, fieldValue], index) => (
                <div key={`custom-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr auto', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input aria-label="Custom field name" placeholder="Field name" value={fieldName} onChange={event => updateCustomField(index, 'name', event.target.value)} style={inputStyle} />
                  <input aria-label="Custom field value" placeholder="Value" value={fieldValue} onChange={event => updateCustomField(index, 'value', event.target.value)} style={inputStyle} />
                  <button type="button" aria-label={`Remove ${fieldName}`} onClick={() => setProductForm(current => ({ ...current, custom_fields: Object.fromEntries(Object.entries(current.custom_fields).filter((_, rowIndex) => rowIndex !== index)) }))} style={{ border: '1px solid var(--light-gray)', borderRadius: '6px', background: 'var(--white)', color: 'var(--danger)', cursor: 'pointer' }}><X size={16} /></button>
                </div>
              ))}
            </section>

            {/* Category */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: '500', fontSize: '0.85rem', marginBottom: '0.35rem' }}>Category</label>
              <select value={productForm.category_id}
                onChange={e => setProductForm({ ...productForm, category_id: e.target.value })}
                style={{ ...inputStyle }}>
                <option value="">— Select Category —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: '500', fontSize: '0.85rem', marginBottom: '0.35rem' }}>Description</label>
              <textarea rows={3} placeholder="Product description..."
                value={productForm.description}
                onChange={e => setProductForm({ ...productForm, description: e.target.value })}
                style={{ ...inputStyle, resize: 'vertical' }}
                onFocus={e => e.target.style.borderColor = 'var(--warm-brown)'}
                onBlur={e => e.target.style.borderColor = 'var(--light-gray)'}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.8rem' }}>
              <button onClick={() => setShowProductModal(false)} style={{
                flex: 1, padding: '0.8rem', border: '1.5px solid var(--light-gray)',
                borderRadius: '10px', background: 'transparent',
                fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem'
              }}>Cancel</button>
              <button onClick={handleSaveProduct} disabled={savingProduct} style={{
                flex: 2, padding: '0.8rem',
                background: savingProduct ? 'var(--mid-gray)' : 'var(--charcoal)',
                color: 'var(--white)', border: 'none',
                borderRadius: '10px', fontWeight: '600',
                cursor: savingProduct ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '6px'
              }}>
                <Check size={16} /> {savingProduct ? 'Saving...' : editingProduct ? 'Update Product' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}